import { Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/services/api";
import type { FreemiumDiscount, UnlockScreenContent } from "@/types";

/** "15% off, until 31 Oct 2026" — null when the discount doesn't apply. */
export function discountLabel(discount: FreemiumDiscount | null): string | null {
  if (!discount?.eligible || !discount.percent_off) return null;
  const percent = `${Math.round(discount.percent_off)}% off`;
  if (!discount.expires_at) return percent;
  const until = new Date(discount.expires_at).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${percent}, until ${until}`;
}

/** The caller's automatic freemium discount, fetched once. */
export function useFreemiumDiscount(enabled = true): FreemiumDiscount | null {
  const [discount, setDiscount] = useState<FreemiumDiscount | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api
      .getFreemiumDiscount()
      .then((d) => !cancelled && setDiscount(d))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return discount;
}

/** Checkout for the soonest joinable paid Cohort (not-yet-started first), else the Cohort list. */
async function nextPaidCohortPath(userId: string): Promise<string> {
  try {
    const programmes = await api.getAvailableProgrammes(userId);
    const paid = programmes
      .filter((p) => p.price > 0 && !p.is_freemium && p.can_join !== false)
      .sort((a, b) => a.start_date.localeCompare(b.start_date));
    const next = paid.find((p) => p.status === "not_started") ?? paid[0];
    if (next) return `/register?programme_id=${encodeURIComponent(next.id)}`;
  } catch {
    // Fall through to the list.
  }
  return "/register";
}

interface UnlockScreenProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  /** Full-page navigation to the purchase flow; injectable for tests. */
  navigateTo?: (path: string) => void;
}

/**
 * The Dominion Free upsell (KC-498): admin-edited copy, what the paid
 * programme unlocks, the automatic discount, and a button into checkout for
 * the next paid Cohort.
 */
export function UnlockScreen({
  open,
  onClose,
  userId,
  navigateTo = (path) => window.location.assign(path),
}: UnlockScreenProps) {
  const [content, setContent] = useState<UnlockScreenContent | null>(null);
  const [isRouting, setIsRouting] = useState(false);
  const discount = useFreemiumDiscount(open);

  useEffect(() => {
    if (!open || content) return;
    api
      .getUnlockScreen()
      .then(setContent)
      .catch(() => {});
  }, [open, content]);

  const offer = discountLabel(discount);

  const handleUnlock = async () => {
    setIsRouting(true);
    navigateTo(await nextPaidCohortPath(userId));
  };

  return (
    <Dialog open={open && content !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        {content && (
          <div className="space-y-5">
            <DialogTitle className="text-2xl font-bold font-['PT_Serif']">
              {content.headline}
            </DialogTitle>
            {content.body && (
              <DialogDescription className="whitespace-pre-line text-base">
                {content.body}
              </DialogDescription>
            )}
            {content.unlocks.length > 0 && (
              <ul className="space-y-2">
                {content.unlocks.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
            {offer && (
              <p className="border border-brand/30 bg-brand/5 px-3 py-2 text-sm font-semibold">
                {offer}
              </p>
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="border border-content-border px-5 py-2.5 text-sm font-semibold hover:bg-content-subtle"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={handleUnlock}
                disabled={isRouting}
                className="flex items-center justify-center gap-2 bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {isRouting && <Loader2 className="h-4 w-4 animate-spin" />}
                {content.button_label}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface FirstVisitEnrollment {
  id: string;
  user_id: string;
  unlock_screen_dismissed_at?: string | null;
  programme: { is_freemium?: boolean };
}

/**
 * Shows the unlock screen once per Free enrolment, on the first dashboard
 * visit. Dismissal is stored on the enrolment so it holds across devices.
 */
export function FirstVisitUnlockScreen({
  enrollment,
}: {
  enrollment: FirstVisitEnrollment | null;
}) {
  const [dismissedLocally, setDismissedLocally] = useState(false);
  const shouldShow =
    enrollment !== null &&
    Boolean(enrollment.programme.is_freemium) &&
    !enrollment.unlock_screen_dismissed_at &&
    !dismissedLocally;

  if (!enrollment || !shouldShow) return null;

  const handleClose = () => {
    setDismissedLocally(true);
    api.dismissUnlockScreen(enrollment.id).catch(() => {});
  };

  return <UnlockScreen open onClose={handleClose} userId={enrollment.user_id} />;
}
